// API resources
import apiPortfolios from '@/api/modules/portfolios'
import apiUsers from '@/api/modules/user'

// Mutations
import {
  ADD_LOANS_LIST,
  ADD_LOANS_COMPOSITION,
  ADD_LOANS_REPARTITION,
  ADD_LOANS_HISTORY,
  ADD_LOANS_COMPLETE_HISTORY,
  RESET_MODULE
} from '@/store/mutation-types'

// Default state
const getDefaultState = () => {
  return {
    list: [],
    metrics: {},
    histories: {},
    loansRepartition: [],
    loansAccountsRepartition: [],
    completeHistory: {},
    composition: {}
  }
}
const state = getDefaultState()

// -----------------------------------------------------------------------------

const actions = {
  fetch (context, cached = true) {
    const { list } = context.state
    if (cached && list.length) {
      return Promise.resolve(list)
    }

    return apiUsers.fetchLoans()
      .then(data => {
        context.commit(ADD_LOANS_LIST, data)
      })
  },

  fetchLoansComposition (context, cached) {
    if (cached && context.state.loansRepartition.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition('loan', 'kind')
      .then(({ data }) => context.commit(ADD_LOANS_COMPOSITION, data))
  },

  fetchLoansRepartition (context, cached) {
    if (cached && context.state.loansAccountsRepartition.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition('loan', 'bank')
      .then(({ data }) => context.commit(ADD_LOANS_REPARTITION, data))
  },

  fetchCompleteHistory (context, { cached = true, subset = 'loan' }) {
    if (cached && context.state.completeHistory[subset]) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfolioCompleteHistory(subset)
      .then(data => context.commit(ADD_LOANS_COMPLETE_HISTORY, { data, subset }))
  },

  fetchHistory (context, { subset = 'loan', from, cached = true }) {
    if (cached && context.state.histories[subset]) {
      return Promise.resolve(context.state.histories[subset])
    }
    return apiUsers.fetchLoansHistory(subset, from)
      .then(history => {
        context.commit(ADD_LOANS_HISTORY, { subset, history })
        return history
      })
  },

  linkLoanToRealEstate (context, { loanId, realEstateId }) {
    return apiPortfolios.linkLoanToRealEstate(loanId, realEstateId)
      .then(() => {
        context.dispatch('fetch', false)
        context.dispatch('realEstates/fetchRealEstateById', { id: realEstateId, cached: false }, { root: true })
      })
  },

  unlinkLoanToRealEstate (context, { loanId, realEstateId }) {
    return apiPortfolios.unlinkLoanToRealEstate(loanId)
      .then(() => {
        context.dispatch('fetch', false)
        context.dispatch('realEstates/fetchRealEstateById', { id: realEstateId, cached: false }, { root: true })
      })
  },

  resetState (context) {
    context.commit(RESET_MODULE)
  }
}

const mutations = {
  [ADD_LOANS_LIST] (state, data) {
    state.metrics = data.metrics
    const sorted = data.loans.sort((a, b) => (a.id < b.id)
      ? 1
      : ((b.id < a.id) ? -1 : 0))
    state.list = sorted
  },

  [ADD_LOANS_COMPOSITION] (state, data) {
    state.loansRepartition = data[0]
  },

  [ADD_LOANS_REPARTITION] (state, data) {
    state.loansAccountsRepartition = data
  },

  [ADD_LOANS_COMPLETE_HISTORY] (state, { data, subset }) {
    state.completeHistory = {
      ...state.completeHistory,
      [subset]: data
    }
  },

  [ADD_LOANS_HISTORY] (state, { subset, history }) {
    state.histories = {
      ...state.histories,
      [subset]: history
    }
  },

  [RESET_MODULE] (state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  getLoansByKindDetail: ({ list }) => (kindDetail) => {
    const portfolios = [...(list.filter(portfolio => portfolio.kindDetail === kindDetail && portfolio.status !== 'hidden' && portfolio.active))]
    return portfolios
  },

  getCumulativeValues: ({ list }) => (kindDetail, key) => {
    const portfolios = [...(list.filter(portfolio => (!kindDetail || portfolio.kindDetail === kindDetail) && portfolio.status !== 'hidden' && portfolio.active))]
      .reduce((sum, { loan: { [key]: value } }) => sum + value, 0)
    return portfolios
  },

  getById: ({ list }) => (id) => {
    // Ignoring rule because sometimes our IDs are stored as strings,
    // sometimes as ints 🤷
    // eslint-disable-next-line eqeqeq
    return list.find(loan => loan.id == id)
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
